import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tabipla/ui";
import { Cloud, CloudOff } from "lucide-react";
import type {
  ExpenseCategory,
  GroupMemberResponseDto,
  CreateExpenseDto,
  MemberType,
} from "@tabipla/shared";
import { useFormDraft } from "@/application/hooks/useFormDraft";
import { DraftBanner } from "@tabipla/components";
import {
  type ExpenseFormDraft,
  memberKey,
  getInitialDraft,
  calculateTotalCustomAmount,
  draftHasUserContent,
  EXPENSE_CATEGORIES,
  buildMemberMap,
} from "@/utils/expense-form";

interface ExpenseFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  members: GroupMemberResponseDto[];
  currentUserId: string;
  onSubmit: (expense: CreateExpenseDto) => void;
  isSubmitting: boolean;
  /** Session ID for unique draft key */
  sessionId: string;
}

export function ExpenseForm({
  open,
  onOpenChange,
  members,
  currentUserId,
  onSubmit,
  isSubmitting,
  sessionId,
}: ExpenseFormProps) {
  const { t } = useTranslation();
  const [showDraftBanner, setShowDraftBanner] = useState(false);

  const {
    data: draft,
    setData: setDraft,
    hasDraft,
    restoreDraft,
    clearDraft,
    saveStatus,
  } = useFormDraft<ExpenseFormDraft>({
    key: `expense-${sessionId}`,
    initialData: getInitialDraft(),
    debounceMs: 500,
    enabled: open,
  });

  // Map to quickly look up member info from composite key
  const memberMap = useMemo(() => buildMemberMap(members), [members]);

  // Convert selectedMemberKeys to Set for easier operations
  const selectedMembers = useMemo(
    () => new Set(draft.selectedMemberKeys),
    [draft.selectedMemberKeys]
  );

  // Check if draft has actual user-entered content (not just default members)
  const hasUserContent = draftHasUserContent(draft);

  // Show draft banner when dialog opens and there's a draft with actual content
  useEffect(() => {
    if (open && hasDraft && hasUserContent) {
      setShowDraftBanner(true);
    } else if (open && !hasUserContent) {
      setShowDraftBanner(false);
    }
  }, [open, hasDraft, hasUserContent]);

  // Initialize selected members with all members when opening without draft
  useEffect(() => {
    if (open && members.length > 0 && draft.selectedMemberKeys.length === 0 && !hasDraft) {
      setDraft((prev) => ({
        ...prev,
        selectedMemberKeys: members.map((m) => memberKey(m.memberId, m.memberType)),
      }));
    }
  }, [open, members, draft.selectedMemberKeys.length, hasDraft, setDraft]);

  const handleRestoreDraft = () => {
    restoreDraft();
    setShowDraftBanner(false);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setShowDraftBanner(false);
    // Initialize with all members
    setDraft({
      ...getInitialDraft(),
      selectedMemberKeys: members.map((m) => memberKey(m.memberId, m.memberType)),
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const parsedAmount = parseInt(draft.amount, 10);
    if (isNaN(parsedAmount) || parsedAmount <= 0) return;

    let shares: { memberId: string; memberType: MemberType; amount: number }[];

    if (draft.splitEqually) {
      const selectedArray = Array.from(selectedMembers);
      const shareAmount = Math.floor(parsedAmount / selectedArray.length);
      const remainder = parsedAmount - shareAmount * selectedArray.length;

      shares = selectedArray.map((key, index) => {
        const member = memberMap.get(key)!;
        return {
          memberId: member.memberId,
          memberType: member.memberType,
          amount: shareAmount + (index < remainder ? 1 : 0),
        };
      });
    } else {
      shares = Array.from(selectedMembers).map((key) => {
        const member = memberMap.get(key)!;
        return {
          memberId: member.memberId,
          memberType: member.memberType,
          amount: parseInt(draft.customAmounts[key] || "0", 10),
        };
      });
    }

    onSubmit({
      amount: parsedAmount,
      description: draft.description,
      category: draft.category,
      date: new Date(draft.date).toISOString(),
      shares,
    });

    // Clear draft after successful submit
    clearDraft();
  };

  const toggleMember = (key: string) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(key)) {
      if (newSelected.size > 1) {
        newSelected.delete(key);
      }
    } else {
      newSelected.add(key);
    }
    setDraft((prev) => ({
      ...prev,
      selectedMemberKeys: Array.from(newSelected),
    }));
  };

  const handleCustomAmountChange = (key: string, value: string) => {
    setDraft((prev) => ({
      ...prev,
      customAmounts: { ...prev.customAmounts, [key]: value },
    }));
  };

  const totalCustomAmount = calculateTotalCustomAmount(draft.customAmounts, selectedMembers);

  const parsedAmount = parseInt(draft.amount, 10) || 0;
  const isValidCustomSplit = !draft.splitEqually && totalCustomAmount === parsedAmount;
  const isValid =
    draft.amount &&
    draft.description &&
    selectedMembers.size > 0 &&
    (draft.splitEqually || isValidCustomSplit);

  // Just close the dialog without clearing the draft
  // Draft will be shown on next open
  const handleClose = () => {
    setShowDraftBanner(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{t("expenses.addExpense")}</DialogTitle>
            {/* Save status indicator */}
            {saveStatus !== "idle" && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                {saveStatus === "saving" ? (
                  <>
                    <CloudOff className="h-3 w-3" />
                    <span>{t("draft.saving")}</span>
                  </>
                ) : (
                  <>
                    <Cloud className="h-3 w-3 text-green-500" />
                    <span>{t("draft.saved")}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Draft banner */}
        {showDraftBanner && (
          <DraftBanner onRestore={handleRestoreDraft} onDiscard={handleDiscardDraft} />
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Amount */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("expenses.amount")}</label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={draft.amount}
                  onChange={(e) => setDraft((prev) => ({ ...prev, amount: e.target.value }))}
                  placeholder={t("expenses.amountPlaceholder")}
                  min="1"
                  required
                />
                <span className="text-sm text-muted-foreground">円</span>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("expenses.description")}</label>
              <Input
                value={draft.description}
                onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
                placeholder={t("expenses.descriptionPlaceholder")}
                required
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("expenses.category")}</label>
              <Select
                value={draft.category}
                onValueChange={(v) =>
                  setDraft((prev) => ({ ...prev, category: v as ExpenseCategory }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {t(`expenses.categories.${cat}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("expenses.date")}</label>
              <Input
                type="date"
                value={draft.date}
                onChange={(e) => setDraft((prev) => ({ ...prev, date: e.target.value }))}
                required
              />
            </div>

            {/* Split type */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("expenses.splitWith")}</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={draft.splitEqually}
                    onChange={() => setDraft((prev) => ({ ...prev, splitEqually: true }))}
                  />
                  <span className="text-sm">{t("expenses.splitEqually")}</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={!draft.splitEqually}
                    onChange={() => setDraft((prev) => ({ ...prev, splitEqually: false }))}
                  />
                  <span className="text-sm">{t("expenses.customSplit")}</span>
                </label>
              </div>
            </div>

            {/* Member selection */}
            <div className="space-y-2">
              <div className="space-y-2">
                {members.map((member) => {
                  const key = memberKey(member.memberId, member.memberType);
                  return (
                    <div
                      key={key}
                      className="flex items-center justify-between rounded-lg border p-2"
                    >
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedMembers.has(key)}
                          onChange={() => toggleMember(key)}
                        />
                        <span className="text-sm">
                          {member.memberName}
                          {member.memberId === currentUserId && member.memberType === "user" && (
                            <span className="ml-1 text-xs text-muted-foreground">
                              ({t("expenses.you")})
                            </span>
                          )}
                        </span>
                      </label>
                      {draft.splitEqually ? (
                        <span className="text-sm text-muted-foreground">
                          {selectedMembers.has(key) && parsedAmount > 0
                            ? `¥${Math.floor(parsedAmount / selectedMembers.size)}`
                            : "-"}
                        </span>
                      ) : (
                        <Input
                          type="number"
                          className="w-24"
                          value={draft.customAmounts[key] || ""}
                          onChange={(e) => handleCustomAmountChange(key, e.target.value)}
                          placeholder="0"
                          disabled={!selectedMembers.has(key)}
                          min="0"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              {!draft.splitEqually && (
                <p
                  className={`text-sm ${
                    totalCustomAmount === parsedAmount ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {t("expenses.total")}: ¥{totalCustomAmount} / ¥{parsedAmount}
                  {totalCustomAmount !== parsedAmount && ` (${t("expenses.mismatch")})`}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting || !isValid}>
              {isSubmitting ? t("expenses.creating") : t("expenses.addExpense")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
